import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export type WebhookStatus = 'active' | 'failed' | 'paused';

export const isPersistedStatus = (status: string): status is WebhookStatus =>
  status === 'active' || status === 'paused' || status === 'failed';

/**
 * Sentinel value for the wildcard "All events" subscription. Stored in
 * EventTypes when EventMode === 'all' so the persisted form value carries
 * the wildcard intent forward (covers existing AND future event types).
 *
 * Backend integration: when writes land, the server should translate this
 * sentinel into either a wildcard subscription row (e.g. an EventType '*'
 * seed) or a boolean column on the webhook.
 */
export const ALL_EVENTS_SENTINEL = '__all__';

/** EventType string used by the backend to identify the wildcard row. */
export const ALL_EVENTS_BACKEND_KEY = '*';

/**
 * Payload schema version sent on every create/update. The UI does not expose
 * this — there is only one version today. Bump when the webhook payload
 * contract changes.
 */
export const WEBHOOK_API_VERSION = 'v1';

export const useWebhookSchema = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'webhooks.form.validation',
  });

  return z
    .object({
      Title: z.string().min(1, { message: t('title_required') }),
      WebhookUrl: z
        .string()
        .url({ message: t('url_invalid') })
        .refine((u) => u.startsWith('https://'), {
          message: t('url_https_required'),
        }),
      Description: z
        .string()
        .optional()
        .transform((e) => (e === '' ? undefined : e)),
      Status: z.enum(['active', 'paused']),
      EventMode: z.enum(['all', 'select']),
      // value can be a real event-type UUID or the ALL_EVENTS_SENTINEL.
      EventTypes: z.array(
        z.object({
          value: z.string(),
          label: z.string(),
        })
      ),
    })
    .superRefine((value, ctx) => {
      if (value.EventMode === 'select' && value.EventTypes.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['EventTypes'],
          message: t('events_required'),
        });
      }
    });
};

export type WebhookFormFields = z.infer<ReturnType<typeof useWebhookSchema>>;

export const defaultValues: WebhookFormFields = {
  Title: '',
  WebhookUrl: '',
  Status: 'active',
  EventMode: 'select',
  EventTypes: [],
};
